import { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { UserScreenNode } from '@/shared/types/system';
import { NavIcon } from './NavIcon';
import { FavoriteStar } from './FavoriteStar';
import { isLeaf } from './useMenu';
import { TruncatedText } from '@/shared/ui/TruncatedText';
import { usePrefetchScreenBundle } from '@/entities/metadata/api/useScreenBundle';

interface Props {
  node: UserScreenNode;
  depth: number;
  forceOpen?: boolean;
  activeScreenId: string | null;
  onOpen: (node: UserScreenNode) => void;
}

/** Seviye başına girinti (#480): 12 px'te klasör ve alt ekranları neredeyse aynı hizadaydı. */
const INDENT_REM = 1.25;
const BASE_REM = 0.5;

/** Alt ağaçta aktif ekran var mı — üst klasörler hafifçe vurgulanır ("nerede olduğumu gör"). */
function containsScreen(node: UserScreenNode, screenId: string | null): boolean {
  if (!screenId) return false;
  return node.children.some((c) => c.screenId === screenId || containsScreen(c, screenId));
}

/** n-seviye menü düğümü: klasör (aç/kapat) ya da yaprak (ekran aç + favori yıldızı). */
export function NavNode({ node, depth, forceOpen, activeScreenId, onOpen }: Props) {
  const [open, setOpen] = useState(false);
  const leaf = isLeaf(node);
  const expanded = forceOpen || open;
  // İmleç satıra gelince ekranın demeti inmeye başlar: tıklandığında çoğu zaman hazırdır.
  const prefetch = usePrefetchScreenBundle();
  const pad = { paddingInlineStart: `${BASE_REM + depth * INDENT_REM}rem` };
  const holdsActive = useMemo(
    () => !leaf && containsScreen(node, activeScreenId),
    [leaf, node, activeScreenId],
  );

  if (leaf) {
    const active = node.screenId === activeScreenId;
    return (
      // Düğme değil, düğme rolündeki satır: içindeki favori yıldızı da bir düğme ve iç içe
      // <button> geçersiz HTML (React uyarı veriyordu, klavye/ekran okuyucu davranışı belirsizdi).
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpen(node)}
        onPointerEnter={() => prefetch(node.screenId)}
        onFocus={() => prefetch(node.screenId)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(node);
          }
        }}
        style={pad}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'group hover:bg-bg-hover focus-visible:ring-ring/40 flex w-full cursor-pointer items-center gap-2 rounded-md py-1.5 pr-2 text-left text-sm focus-visible:ring-2 focus-visible:outline-none',
          active && 'bg-brand-500/10 text-brand-700 dark:text-brand-400 font-medium',
        )}
      >
        {/* Ok genişliğinde boşluk: ekranlar kardeş klasörlerle aynı sütunda dursun (#480). */}
        <span className="w-3.5 shrink-0" aria-hidden="true" />
        <NavIcon node={node} className="h-4 w-4 shrink-0" />
        <TruncatedText text={node.caption} className="min-w-0 flex-1" />
        <FavoriteStar screenId={node.screenId} favorite={node.favorite} />
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={pad}
        aria-expanded={expanded}
        // Klasör kalın, ekran normal (#480); aktif ekranın üst klasörleri marka renginde.
        className={cn(
          'hover:bg-bg-hover hover:text-fg flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left text-sm font-semibold',
          holdsActive ? 'text-brand-700 dark:text-brand-400' : 'text-fg',
        )}
      >
        <ChevronRight
          className={cn('text-fg-subtle h-3.5 w-3.5 shrink-0 transition-transform', expanded && 'rotate-90')}
        />
        <NavIcon node={node} className="h-4 w-4 shrink-0" />
        <TruncatedText text={node.caption} className="min-w-0 flex-1" />
      </button>
      {expanded ? (
        <div role="group" className="relative">
          {/* Kılavuz çizgisi: klasörün okunun hizasından alt öğeleri gruplar (#480). */}
          <span
            aria-hidden="true"
            className="bg-border pointer-events-none absolute top-0 bottom-1 w-px"
            style={{ left: `calc(${BASE_REM + depth * INDENT_REM}rem + 7px)` }}
          />
          {node.children.map((c) => (
            <NavNode
              key={c.screenId}
              node={c}
              depth={depth + 1}
              forceOpen={forceOpen}
              activeScreenId={activeScreenId}
              onOpen={onOpen}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
